import React, { useState, useRef, useEffect } from "react";
import { Box, InputAdornment, SvgIcon } from "@mui/material";
import { StriimTypography, StriimInputField, StriimTooltip, StriimChip, StriimButton } from "@striim/striim-ui";
import { useNavigate } from "react-router";
import App from "app";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import {
    selectIsConfigured,
    selectIsHealthy,
    selectIsSidebarOpen,
    setSidebarOpenAction
} from "src/stores/redux/features/striim-ai/striim-ai.slice";
import { AIAssistant as AIAssistantSvg, PlugConnected as PlugConnectedSvg } from "../../../generic/icon/customIcons";
import ArrowForwardSvg from "../../../generic/icon/customIcons/Arrow_Forward.svg";
import styles from "./hero-ai-input.styles";
import AiGradientButton from "src/modules/striim-ai-sidebar/components/ai-gradient-button/ai-gradient-button";
import { AI_AGENT_NAME } from "src/modules/striim-ai-sidebar/components/ai-config-screen/ai-config-screen";
import STRIIM_MCP_ROUTES from "../../striim-mcp/routes.json";
import licenseService from "core/services/metaStoreService/license-service";

// `*.svg` imports are typed as strings but resolve to React components via @svgr/webpack.
const AIAssistant = (AIAssistantSvg as unknown) as React.ElementType;
const PlugConnected = (PlugConnectedSvg as unknown) as typeof AIAssistant;
const RightArrowIcon = (ArrowForwardSvg as unknown) as React.FC<React.SVGProps<SVGSVGElement>>;

const EXAMPLE_QUERIES = [
    "Build a real-time pipeline from SQL Server to Postgres for our AI architecture",
    "Ingest Oracle data into BigQuery as a foundation for Generative AI",
    "Manage my Striim users",
    "Migrate Oracle to Databricks with zero downtime and keep it in sync",
    "Monitor my cluster health and alert me if something looks off",
    "Show me all running apps and which ones are falling behind"
];

const Header: React.FC = () => {
    return (
        <Box>
            <Box sx={styles.headerTitleRow}>
                <SvgIcon component={AIAssistant} sx={styles.headerIcon} />
                <StriimTypography variant="h2" color="primary.700">
                    Explore Striim using {AI_AGENT_NAME}
                </StriimTypography>
            </Box>
            <Box sx={styles.headerSubtitleRow}>
                <StriimTypography variant="body4" color="greyscale.800">
                    What would you like to get started with today?
                </StriimTypography>
            </Box>
        </Box>
    );
};

const McpPromoPill: React.FC = () => {
    const navigate = useNavigate();
    const goToMcpPage = () => navigate(STRIIM_MCP_ROUTES.mcpConsole);

    return (
        <Box sx={styles.mcpPromo} data-testid="mcp-promo-pill">
            <Box sx={styles.mcpPromoIconBadge}>
                <SvgIcon component={PlugConnected} sx={styles.mcpPromoIcon} />
            </Box>
            <Box sx={styles.mcpPromoText}>
                <Box sx={styles.mcpPromoHeadlineRow}>
                    <StriimChip
                        type="tag-chip"
                        label={
                            <StriimTypography variant="caption4" color="secondary.500">
                                NEW
                            </StriimTypography>
                        }
                        hideAvatar
                        sx={styles.mcpPromoBadge}
                        clickable={false}
                        data-testid="mcp-promo-badge"
                    />
                    <StriimTypography variant="body4" color="primary.700" sx={styles.mcpPromoHeadline}>
                        Build, run, and monitor pipelines via Striim MCP Server
                    </StriimTypography>
                </Box>
                <StriimTypography variant="caption" color="greyscale.700">
                    Connect your favorite AI assistant to Striim.
                </StriimTypography>
            </Box>
            <StriimButton variant="secondary" onClick={goToMcpPage} sx={styles.mcpPromoCta} data-testid="mcp-promo-cta">
                Try it now
            </StriimButton>
        </Box>
    );
};

const HeroAIInput: React.FC = () => {
    const dispatch = useAppDispatch();
    const isConfigured = useAppSelector(selectIsConfigured);
    const isHealthy = useAppSelector(selectIsHealthy);
    const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
    const isDisabled = !isConfigured || !isHealthy;
    const disabledTooltip = !isConfigured
        ? `${AI_AGENT_NAME} needs to be configured before you can use it.`
        : !isHealthy
        ? `${AI_AGENT_NAME} is not reachable. Please check your configuration.`
        : "";
    const [value, setValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    const [displayPlaceholder, setDisplayPlaceholder] = useState("");
    const [queryIndex, setQueryIndex] = useState(0);
    const [charIndex, setCharIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (isSidebarOpen) {
            setValue("");
        }
    }, [isSidebarOpen]);

    useEffect(() => {
        if (value.trim() || isFocused || isSidebarOpen) return;

        const current = EXAMPLE_QUERIES[queryIndex];

        if (!isDeleting && charIndex === current.length) {
            const t = setTimeout(() => setIsDeleting(true), 1800);
            return () => clearTimeout(t);
        }

        if (isDeleting && charIndex === 0) {
            const t = setTimeout(() => {
                setIsDeleting(false);
                setQueryIndex(i => (i + 1) % EXAMPLE_QUERIES.length);
            }, 400);
            return () => clearTimeout(t);
        }

        const delay = isDeleting ? 5 : 10;
        const t = setTimeout(() => {
            const next = charIndex + (isDeleting ? -1 : 1);
            setCharIndex(next);
            setDisplayPlaceholder(current.slice(0, next));
        }, delay);
        return () => clearTimeout(t);
    }, [charIndex, isDeleting, queryIndex, value, isFocused, isSidebarOpen]);

    const handleSubmit = () => {
        if (isDisabled) return;
        const text = value.trim();
        if (!text) return;
        setValue("");
        dispatch(setSidebarOpenAction(true));

        setTimeout(() => {
            App.vent.trigger("striimAI:user_message", text);
        }, 1000);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <Box sx={styles.container}>
            <Box mb="16px">
                <Header />
            </Box>

            <StriimTooltip
                title={isDisabled ? disabledTooltip : ""}
                placement="top"
                arrow
                disableHoverListener={!isDisabled}
            >
                <Box sx={styles.inputWrapper}>
                    <StriimInputField
                        inputRef={inputRef}
                        fullWidth
                        multiline
                        maxRows={4}
                        disabled={isDisabled}
                        placeholder={value || isFocused || isSidebarOpen ? "" : displayPlaceholder}
                        value={value}
                        onChange={(val: string) => setValue(val)}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        InputProps={{
                            onKeyDown: handleKeyDown,
                            sx: styles.input
                        }}
                    />
                    <Box sx={styles.sendButtonWrapper}>
                        <AiGradientButton
                            icon={<RightArrowIcon />}
                            onClick={handleSubmit}
                            disabled={isDisabled || !value.trim()}
                        />
                    </Box>
                </Box>
            </StriimTooltip>

            {licenseService?.isMCPEnabled && <McpPromoPill />}
        </Box>
    );
};

export default HeroAIInput;
